import { Button } from '../../../components/ui/button';
import { GiftsBlock, RsvpBlock, renderGalleryByPosition } from '../shared/TemplateBlocks';

const getLocationSummary = (site, locationCards) => {
  if (site.same_location && locationCards?.[0]) {
    return {
      ceremony: locationCards[0],
      reception: {
        title: 'Recepção',
        location: 'Mesmo local',
        address: locationCards[0].address || locationCards[0].location,
        time: site.reception_time || site.ceremony_time
      }
    };
  }

  return {
    ceremony: locationCards?.[0] || {
      title: 'Cerimônia',
      location: site.ceremony_location,
      address: site.ceremony_address,
      time: site.ceremony_time
    },
    reception: locationCards?.[1] || {
      title: 'Recepção',
      location: site.reception_location,
      address: site.reception_address,
      time: site.reception_time
    }
  };
};

export default function NoirDoradoTemplate(props) {
  const {
    site,
    theme,
    weddingDateLabel,
    daysUntil,
    locationCards,
    availableGifts,
    galleryPosition,
    isTemplatePreview,
    onTemplatePreviewRedirect,
    onOpenRsvp,
    onGiftClick,
    onOpenGallery,
    showCountdown,
    showGiftSection,
    showRsvpCount,
    confirmedGuests
  } = props;

  const primary = theme.primaryColor || '#0A0804';
  const secondary = theme.secondaryColor || '#C9A84C';
  const soft = theme.bannerSectionBackground || '#E8D5A3';
  const page = theme.storySectionBackground || '#F7F0E3';
  const titleColor = theme.heroTitleColor || primary;
  const bodyColor = theme.bannerTextColor || theme.bodyTextColor || primary;
  const { ceremony, reception } = getLocationSummary(site, locationCards);

  const details = [
    {
      label: 'Cerimônia',
      value: ceremony?.location || ceremony?.title || 'Local da cerimônia',
      sub: [ceremony?.time, ceremony?.address].filter(Boolean).join(' · ')
    },
    {
      label: 'Recepção',
      value: reception?.location || reception?.title || 'Local da recepção',
      sub: [reception?.time, reception?.address].filter(Boolean).join(' · ')
    },
    {
      label: showRsvpCount ? 'Confirmados' : 'Contagem',
      value: showRsvpCount ? String(confirmedGuests || 0) + ' pessoa(s)' : String(daysUntil) + ' dias',
      sub: showRsvpCount ? 'Já confirmaram presença' : 'Até o grande dia'
    }
  ];

  const galleryNode = site.gallery_images?.length ? (
    <section style={{ background: theme.gallerySectionBackground || page }}>
      <div className="grid grid-cols-2 md:grid-cols-4">
        {site.gallery_images.slice(0, 8).map((img, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => onOpenGallery?.(idx)}
            className="group h-44 overflow-hidden md:h-72"
            aria-label={'Abrir foto ' + (idx + 1)}
          >
            <img
              src={img}
              alt={'Galeria ' + (idx + 1)}
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
            />
          </button>
        ))}
      </div>
    </section>
  ) : null;

  const openRsvp = () => (isTemplatePreview ? onTemplatePreviewRedirect?.() : onOpenRsvp?.());

  return (
    <>
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-20 text-center" style={{ background: 'radial-gradient(circle at 50% 42%, ' + secondary + '22, transparent 34%), linear-gradient(135deg, ' + primary + ', #1A1510)' }}>
        {[0, 1, 2, 3, 4, 5].map((dot) => (
          <span key={dot} className="absolute h-1 w-1 rounded-full" style={{ background: secondary, left: (14 + dot * 14) + '%', top: (18 + (dot % 3) * 22) + '%', opacity: 0.55 }} />
        ))}
        <div className="relative z-10 max-w-5xl border px-8 py-16 md:px-20" style={{ borderColor: secondary + '88' }}>
          <p className="mb-8 text-[0.62rem] uppercase tracking-[0.5em]" style={{ color: secondary, fontFamily: theme.bannerTextFont }}>Noir Dorado</p>
          <h1 className="text-5xl leading-none md:text-7xl lg:text-8xl" style={{ color: soft, fontFamily: theme.bannerTitleFont || theme.headingFont }}>{site.couple_names}</h1>
          <div className="mx-auto my-8 h-px w-20" style={{ background: secondary }} />
          <p className="text-xs uppercase tracking-[0.24em]" style={{ color: soft, fontFamily: theme.bannerTextFont, opacity: 0.72 }}>{weddingDateLabel}</p>
          {showCountdown ? <p className="mt-5 text-sm uppercase tracking-[0.28em]" style={{ color: secondary }}>{daysUntil} dias</p> : null}
          <Button
              onClick={openRsvp}
              className="mt-10 rounded-none px-10 uppercase tracking-[0.24em]"
              style={{
                background: theme.confirmButtonBackground || primary,
                color: theme.confirmButtonText || soft,
                borderColor: theme.confirmButtonBorder || primary,
                borderWidth: 1,
                borderStyle: 'solid'
              }}
            >
              Confirmar Presença
            </Button>
        </div>
      </section>
      {renderGalleryByPosition(galleryPosition, 'after_hero', galleryNode)}
      <section className="px-6 py-20" style={{ background: page }}>
        <div className="mx-auto grid max-w-6xl gap-10 md:grid-cols-[0.95fr_1.05fr]">
          <div className="min-h-[430px] border p-4" style={{ borderColor: secondary }}>
            {theme.heroImage ? <img src={theme.heroImage} alt={site.couple_names} className="h-full min-h-[400px] w-full object-cover" /> : <div className="flex h-full min-h-[400px] items-center justify-center text-xs uppercase tracking-[0.24em]" style={{ color: primary }}>Foto do casal</div>}
          </div>
          <div className="flex flex-col justify-center">
            <p className="mb-5 text-[0.62rem] uppercase tracking-[0.46em]" style={{ color: secondary }}>Nossa história</p>
            <h2 className="mb-6 text-4xl italic md:text-5xl" style={{ color: theme.storyTitleColor || primary, fontFamily: theme.storyTitleFont || theme.headingFont }}>Um convite em dourado</h2>
            <p className="whitespace-pre-line text-base leading-8" style={{ color: theme.storyBodyColor || bodyColor, fontFamily: theme.storyBodyFont }}>{site.story_text}</p>
          </div>
        </div>
      </section>
      {renderGalleryByPosition(galleryPosition, 'after_story', galleryNode)}
      <section className="px-6 py-20 " style={{ background: theme.locationSectionBackground || primary }}>
        <h2
          className="mb-12 text-center text-3xl italic md:text-4xl"
          style={{ color: theme.locationTitleColor || soft, fontFamily: theme.locationTitleFont || theme.headingFont }}
        >
          O grande dia
        </h2>
        <div className="mx-auto grid max-w-5xl gap-px md:grid-cols-3" style={{ background: 'rgba(255,255,255,0.12)' }}>
          {details.map((item) => (
            <div key={item.label} className="p-8 text-center" style={{ background: theme.locationCardBackground || 'rgba(255,255,255,0.04)' }}>
              <p className="mb-4 text-[0.58rem] uppercase tracking-[0.4em]" style={{ color: theme.locationIconColor || secondary, fontFamily: theme.locationTextFont }}>{item.label}</p>
              <p className="mb-3 text-xl italic" style={{ color: theme.locationTitleColor || soft, fontFamily: theme.locationTitleFont || theme.headingFont }}>{item.value}</p>
              <p className="text-xs leading-6" style={{ color: theme.locationTextColor || 'rgba(255,255,255,0.62)', fontFamily: theme.locationTextFont }}>{item.sub}</p>
            </div>
          ))}
        </div>
      </section>
      {renderGalleryByPosition(galleryPosition, 'before_gifts', galleryNode)}

      <GiftsBlock
        gifts={availableGifts}
        headingFont={theme.headingFont}
        theme={theme}
        titleColor={primary}
        textColor={theme.bodyTextColor}
        secondaryColor={secondary}
        onGiftClick={onGiftClick}
        compact
        showGiftSection={showGiftSection}
      />

      {renderGalleryByPosition(galleryPosition, 'end', galleryNode)}

      <RsvpBlock
        onOpenRsvp={onOpenRsvp}
        isTemplatePreview={isTemplatePreview}
        onTemplatePreviewRedirect={onTemplatePreviewRedirect}
        primaryColor={primary}
        secondaryColor={secondary}
        sectionBackground={theme.rsvpSectionBackground || soft}
        buttonBackground={theme.confirmButtonBackground || primary}
        buttonText={theme.confirmButtonText || soft}
        buttonBorder={theme.confirmButtonBorder || primary}
        iconColor={theme.rsvpIconColor || secondary}
        titleColor={theme.rsvpTextColor || primary}
        bodyColor={theme.rsvpTextColor || bodyColor}
        countColor={theme.rsvpTextColor || bodyColor}
        headingFont={theme.rsvpTitleFont || theme.headingFont}
        bodyFont={theme.rsvpTextFont || theme.bannerTextFont}
        showRsvpCount={showRsvpCount}
        confirmedGuests={confirmedGuests}
      />

      <footer className="px-6 py-12 text-center" style={{ background: primary }}>
        <div className="text-3xl italic" style={{ color: soft, fontFamily: theme.headingFont }}>
          {site.couple_names}
        </div>
        <div className="mt-2 text-[0.65rem] uppercase tracking-[0.35em]" style={{ color: 'rgba(255,255,255,0.42)', fontFamily: theme.bannerTextFont }}>
          Virei Noiva
        </div>
      </footer>
    </>
  );
}

